import { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Tooltip,
  Select,
  Segmented,
  Tag,
  Tour,
  Upload,
  message,
} from "antd";
import {
  CheckCircleFilled,
  BgColorsOutlined,
  EditOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  IdcardOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  RobotOutlined,
  SaveOutlined,
  FontColorsOutlined,
  UserOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import apiClient from "../../actions/api";
import { loadUser } from "../../actions/auth";
import openNotification from "../../components/Notifications/openNotification";
import { getCountryName } from "../../config/countries";
import isEmpty from "../../validations/is-empty";
import LogoSrc from "../../assets/image/logo.svg";
import "../../style/auth/profile.scss";
import useSpecialActions from "../../hooks/useSpecialActions";

const { Dragger } = Upload;
const { TextArea } = Input;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TITLE_PREFIXES = [
  "Intern",
  "Trainee",
  "Junior",
  "Associate",
  "Mid-Level",
  "Intermediate",
  "Senior",
  "Lead",
  "Principal",
  "Staff",
  "Senior Staff",
  "Architect",
  "Engineering Manager",
  "Director",
  "VP",
  "CTO",
];
const DEFAULT_EMPLOYMENT = () => ({ title_prefix: "", bullet_count: 3 });
const DEFAULT_EMPLOYMENT_PREFERENCES = () => [
  { title_prefix: "Senior", bullet_count: 12 },
  { title_prefix: "Mid-Level", bullet_count: 8 },
];

const shown = (value) =>
  value === undefined || value === null || String(value).trim() === ""
    ? "Not provided"
    : value;

const genderLabel = (value) => {
  const normalized = String(value ?? "").toLowerCase();
  if (["0", "male", "m"].includes(normalized)) return "Male";
  if (["1", "female", "f"].includes(normalized)) return "Female";
  return "Not provided";
};

const Profile = ({ title }) => {
  document.title = title;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const canUseSpecialActions = useSpecialActions();
  const [resumeFile, setResumeFile] = useState("");
  const [coverLetterFile, setCoverLetterFile] = useState("");
  const [personalPrompt, setPersonalPrompt] = useState("");
  const [callPrompt, setCallPrompt] = useState("");
  const [employmentPreferences, setEmploymentPreferences] = useState(
    DEFAULT_EMPLOYMENT_PREFERENCES,
  );
  const [yearsOfExperience, setYearsOfExperience] = useState(5);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateUploadErrors, setTemplateUploadErrors] = useState({
    resume: [],
    coverLetter: [],
  });
  const [visibleErrorTooltip, setVisibleErrorTooltip] = useState(null);
  const [templateTourOpen, setTemplateTourOpen] = useState(false);
  const [documentMode, setDocumentMode] = useState("resume_document");
  const [resumeDocumentUploading, setResumeDocumentUploading] = useState(false);
  const [resumeProfileModalOpen, setResumeProfileModalOpen] = useState(false);
  const [resumeProfileName, setResumeProfileName] = useState("");
  const [editingResumeProfile, setEditingResumeProfile] = useState(null);
  const [removingProfileId, setRemovingProfileId] = useState("");
  const templatesCardRef = useRef(null);
  const resumeTemplateRef = useRef(null);
  const coverLetterTemplateRef = useRef(null);
  const employmentSettingsRef = useRef(null);
  const templateSaveButtonRef = useRef(null);

  useEffect(() => {
    setResumeFile(user?.template_url || "");
    setCoverLetterFile(user?.template_coverletter_url || "");
    setPersonalPrompt(user?.personal_prompt || "");
    setCallPrompt(user?.call_prompt || "");
    setEmploymentPreferences(
      Array.isArray(user?.employment_preferences) &&
        user.employment_preferences.length >= 2
        ? user.employment_preferences.map(
            ({ title_prefix = "", bullet_count = 3 }) => ({
              title_prefix,
              bullet_count,
            }),
          )
        : DEFAULT_EMPLOYMENT_PREFERENCES(),
    );
    setYearsOfExperience(user?.years_of_experience ?? 5);
  }, [user]);

  useEffect(() => {
    if (!user?._id) return undefined;
    const tourKey = `resumeBuilderTemplateTour:${user._id}`;
    if (localStorage.getItem(tourKey) !== "complete") {
      const timer = window.setTimeout(() => setTemplateTourOpen(true), 500);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [user?._id]);

  const closeTemplateTour = () => {
    if (user?._id) {
      localStorage.setItem(`resumeBuilderTemplateTour:${user._id}`, "complete");
    }
    setTemplateTourOpen(false);
  };

  const uploadProps = (kind) => {
    const isResume = kind === "resume";
    const currentFile = isResume
      ? user?.template_url
      : user?.template_coverletter_url;
    return {
      name: isResume ? "file" : "coverletterfile",
      multiple: false,
      maxCount: 1,
      action: `${API_BASE_URL}/api/bids/${
        isResume ? "template-upload" : "template-coverletter-upload"
      }`,
      accept: ".docx",
      data: isResume
        ? { employment_count: employmentPreferences.length }
        : undefined,
      defaultFileList: isEmpty(currentFile)
        ? []
        : [
            {
              uid: kind,
              name: currentFile,
              status: "done",
              url: `${API_BASE_URL}/template/${currentFile}`,
            },
          ],
      onChange(info) {
        setUploading(info.file.status === "uploading");
        if (info.file.status === "uploading") {
          setVisibleErrorTooltip(null);
          setTemplateUploadErrors((current) => ({
            ...current,
            [kind]: [],
          }));
        }
        if (info.file.status === "done") {
          setVisibleErrorTooltip(null);
          const filename = info.file.response?.filename;
          if (isResume) setResumeFile(filename);
          else setCoverLetterFile(filename);
          setTemplateUploadErrors((current) => ({
            ...current,
            [kind]: [],
          }));
          message.success(`${info.file.name} uploaded.`);
        } else if (info.file.status === "error") {
          const errors = info.file.response?.errors;
          const errorMessages =
            Array.isArray(errors) && errors.length > 0
              ? errors.map(({ msg }) => msg)
              : [`${info.file.name} upload failed.`];
          setTemplateUploadErrors((current) => ({
            ...current,
            [kind]: errorMessages,
          }));
          setVisibleErrorTooltip(kind);
          if (Array.isArray(errors) && errors.length > 0) {
            message.error(
              `${isResume ? "Resume" : "Cover letter"} template validation failed.${
                isResume ? " Check the warning icon for details." : ""
              }`,
            );
          } else {
            message.error(`${info.file.name} upload failed.`);
          }
        }
      },
    };
  };

  const resumeDocumentUploadProps = {
    name: "resume_document",
    multiple: false,
    maxCount: 1,
    accept: ".docx",
    showUploadList: false,
    customRequest: async ({ file, onError, onProgress, onSuccess }) => {
      const data = new FormData();
      data.append("resume_document", file);
      data.append("profile_name", resumeProfileName.trim());
      setResumeDocumentUploading(true);
      try {
        const response = await apiClient.request({
          method: editingResumeProfile ? "put" : "post",
          url: editingResumeProfile
            ? `/users/resume-document/${editingResumeProfile._id}`
            : "/users/resume-document-upload",
          data,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: ({ loaded, total }) =>
            onProgress({ percent: total ? (loaded / total) * 100 : 0 }),
        });
        await dispatch(loadUser());
        onSuccess(response.data);
        setResumeProfileModalOpen(false);
        setResumeProfileName("");
        setEditingResumeProfile(null);
        message.success(
          editingResumeProfile
            ? "Resume profile updated."
            : "Resume document uploaded and saved.",
        );
      } catch (error) {
        onError(error);
        openNotification(
          "error",
          "Upload failed",
          error?.response?.data?.errors?.[0]?.msg ||
            "Unable to upload the resume document.",
        );
      } finally {
        setResumeDocumentUploading(false);
      }
    },
  };

  const saveProfileContent = async () => {
    if (isEmpty(resumeFile) || isEmpty(coverLetterFile)) {
      openNotification(
        "error",
        "Templates required",
        "Please upload both a resume and cover letter template.",
      );
      return;
    }
    if (isEmpty(callPrompt)) {
      openNotification(
        "error",
        "Call prompt required",
        "Please enter a call prompt.",
      );
      return;
    }
    if (
      employmentPreferences.length < 2 ||
      employmentPreferences.some((employment) =>
        isEmpty(employment.title_prefix),
      )
    ) {
      openNotification(
        "error",
        "Employment settings required",
        "Please add at least two employment entries and select a title prefix for each one.",
      );
      return;
    }
    if (
      !Number.isInteger(yearsOfExperience) ||
      yearsOfExperience < 0 ||
      yearsOfExperience > 100
    ) {
      openNotification(
        "error",
        "Years of experience required",
        "Please enter a whole number between 0 and 100.",
      );
      return;
    }

    setSaving(true);
    try {
      await apiClient.post("/users/update-profile", {
        filename: resumeFile,
        coverletterfilename: coverLetterFile,
        personal_prompt: personalPrompt,
        call_prompt: callPrompt,
        employment_preferences: employmentPreferences,
        years_of_experience: yearsOfExperience,
      });
      await dispatch(loadUser());
      openNotification(
        "success",
        "Profile saved",
        "Templates, employment settings, and prompts were updated.",
      );
    } catch (error) {
      const errors = error?.response?.data?.errors;
      if (Array.isArray(errors)) {
        errors.forEach(({ msg }) =>
          openNotification("error", "Unable to save", msg),
        );
      } else {
        openNotification("error", "Unable to save", "Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const removeResumeProfile = async (profile) => {
    setRemovingProfileId(profile._id);
    try {
      const { data } = await apiClient.delete(
        `/users/resume-document/${profile._id}`,
      );
      await dispatch(loadUser());
      message.success(
        `${profile.name} and ${data.removedApplications || 0} related application(s) were removed.`,
      );
    } catch (error) {
      openNotification(
        "error",
        "Unable to remove profile",
        error?.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setRemovingProfileId("");
    }
  };

  const saveResumeProfileName = async () => {
    if (!editingResumeProfile || !resumeProfileName.trim()) return;
    setResumeDocumentUploading(true);
    try {
      await apiClient.put(
        `/users/resume-document/${editingResumeProfile._id}`,
        { profile_name: resumeProfileName.trim() },
      );
      await dispatch(loadUser());
      setResumeProfileModalOpen(false);
      setEditingResumeProfile(null);
      setResumeProfileName("");
      message.success("Resume profile updated.");
    } catch (error) {
      openNotification(
        "error",
        "Unable to update profile",
        error?.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setResumeDocumentUploading(false);
    }
  };

  const details = [
    { label: "Email", value: shown(user?.email), icon: <MailOutlined /> },
    {
      label: "Phone number",
      value: shown(user?.phone),
      icon: <PhoneOutlined />,
    },
    {
      label: "Gender",
      value: genderLabel(user?.gender),
      icon: <IdcardOutlined />,
    },
    {
      label: "Country",
      value: shown(getCountryName(user?.job_type)),
      icon: <EnvironmentOutlined />,
    },
    {
      label: "Background color",
      value: shown(user?.background_color),
      color: user?.background_color,
      icon: <BgColorsOutlined />,
    },
    {
      label: "Text color",
      value: shown(user?.text_color),
      color: user?.text_color,
      icon: <FontColorsOutlined />,
    },
  ];

  const documentModeSelector = (
    <div className="profile-document-mode">
      <span>Document source</span>
      <Segmented
        className="resume-source-segmented"
        value={documentMode}
        onChange={setDocumentMode}
        options={[
          { label: "Templates", value: "templates" },
          { label: "Profiles", value: "resume_document" },
        ]}
      />
    </div>
  );

  return (
    <div className="profileSection">
      <header className="profile-heading">
        <span className="profile-title-icon">
          <UserOutlined />
        </span>
        <div>
          <h2>View Profile</h2>
          <p>
            Review account details and manage your document templates and AI
            prompts.
          </p>
        </div>
      </header>

      <div className="profile-workspace">
        <aside className="profile-account-column">
          <div className="profile-identity-panel">
            <img
              src={user?.avatar || LogoSrc}
              alt={`${user?.name || "User"} profile`}
            />
            <div>
              <span>Personal account</span>
              <h3>{shown(user?.name)}</h3>
              <p>{shown(user?.email)}</p>
              <Tag color={user?.email_verified ? "success" : "default"}>
                {user?.email_verified
                  ? "Verified account"
                  : "Verification pending"}
              </Tag>
            </div>
          </div>
          <div className="profile-readonly-panel">
            <div className="profile-card-heading">
              <div>
                <strong>Account information</strong>
                <span>Manage these details from Account Settings.</span>
              </div>
              <div className="profile-account-actions">
                <Tag icon={<CheckCircleFilled />}>Read only</Tag>
                <Link
                  className="profile-edit-account"
                  to="/account-settings"
                  aria-label="Edit account information"
                >
                  <EditOutlined /> Edit
                </Link>
              </div>
            </div>
            <div className="profile-details-grid">
              <div className="profile-detail" aria-label="Full name">
                <span>
                  <UserOutlined />
                </span>
                <strong>{shown(user?.name)}</strong>
              </div>
              {details.map((detail) => (
                <div
                  className="profile-detail"
                  key={detail.label}
                  aria-label={detail.label}
                >
                  <span>{detail.icon}</span>
                  <strong>
                    {detail.color && (
                      <i style={{ backgroundColor: detail.color }} />
                    )}
                    {detail.value}
                  </strong>
                </div>
              ))}
            </div>
            <div className="profile-color-preview">
              <div className="profile-preview-heading">
                <strong>Appearance preview</strong>
                <span>Background and text color</span>
              </div>
              <div
                className="profile-preview-swatch"
                style={{
                  backgroundColor: user?.background_color || "#087b85",
                  color: user?.text_color || "#ffffff",
                }}
              >
                <div className="profile-preview-avatar">
                  <img
                    src={user?.avatar || LogoSrc}
                    alt="Profile appearance preview"
                  />
                </div>
                <div>
                  <strong>{shown(user?.name)}</strong>
                  <span>Resume Builder profile</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {documentMode === "resume_document" ? (
          <div className="profile-content-form">
            {documentModeSelector}
            <section className="profile-editor-card profile-resume-document-card">
              <div className="profile-card-heading">
                <div>
                  <strong>
                    <FileTextOutlined /> Resume document
                  </strong>
                  <span>
                    Upload your existing resume as a DOCX file. It does not need
                    template variables.
                  </span>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingResumeProfile(null);
                    setResumeProfileName("");
                    setResumeProfileModalOpen(true);
                  }}
                >
                  Add profile
                </Button>
              </div>
              <div className="resume-profile-list">
                {(user?.resume_documents || []).map((profile) => (
                  <div className="resume-profile-card" key={profile._id}>
                    <FileTextOutlined />
                    <button
                      className="resume-profile-details"
                      type="button"
                      onClick={() => {
                        setEditingResumeProfile(profile);
                        setResumeProfileName(profile.name);
                        setResumeProfileModalOpen(true);
                      }}
                    >
                      <strong>{profile.name}</strong>
                      <span>{profile.filename}</span>
                    </button>
                    <Popconfirm
                      title="Remove resume profile?"
                      description="If you remove this profile, all related applications will be permanently deleted and connot be recovered."
                      okText="Remove permanently"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={() => removeResumeProfile(profile)}
                    >
                      <Button
                        className="resume-profile-remove"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        loading={removingProfileId === profile._id}
                        aria-label={`Remove ${profile.name}`}
                      />
                    </Popconfirm>
                  </div>
                ))}
                {(user?.resume_documents || []).length === 0 && (
                  <div className="resume-profile-empty">
                    No resume profiles yet. Select Add profile to upload one.
                  </div>
                )}
              </div>
            </section>
            <Modal
              title={
                editingResumeProfile
                  ? "Edit resume profile"
                  : "Add resume profile"
              }
              open={resumeProfileModalOpen}
              footer={
                editingResumeProfile
                  ? [
                      <Button
                        key="cancel"
                        onClick={() => setResumeProfileModalOpen(false)}
                      >
                        Cancel
                      </Button>,
                      <Button
                        key="save"
                        type="primary"
                        loading={resumeDocumentUploading}
                        disabled={!resumeProfileName.trim()}
                        onClick={saveResumeProfileName}
                      >
                        Save name
                      </Button>,
                    ]
                  : null
              }
              onCancel={() => {
                setResumeProfileModalOpen(false);
                setEditingResumeProfile(null);
                setResumeProfileName("");
              }}
              destroyOnHidden
            >
              <label className="resume-profile-name-field">
                <span>Profile name</span>
                <Input
                  value={resumeProfileName}
                  onChange={(event) => setResumeProfileName(event.target.value)}
                  placeholder="For example: Software Engineer"
                />
              </label>
              <Dragger
                {...resumeDocumentUploadProps}
                disabled={!resumeProfileName.trim() || resumeDocumentUploading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {editingResumeProfile
                    ? "Click or drag a replacement DOCX here"
                    : "Click or drag your resume DOCX here"}
                </p>
                <p className="ant-upload-hint">
                  {editingResumeProfile
                    ? "Uploading replaces the saved document and applies the profile name above."
                    : "Enter a profile name before uploading."}
                </p>
              </Dragger>
            </Modal>
          </div>
        ) : (
          <Form className="profile-content-form" onFinish={saveProfileContent}>
            {documentModeSelector}
            <section
              className="profile-editor-card profile-templates-card"
              ref={templatesCardRef}
            >
              <div className="profile-card-heading">
                <div>
                  <strong>
                    <FileTextOutlined /> Document templates
                  </strong>
                  <span>
                    Manage DOCX files and employment settings used to generate
                    applications.
                  </span>
                </div>
                <div className="profile-template-heading-actions">
                  <Button
                    htmlType="button"
                    onClick={() => setTemplateTourOpen(true)}
                  >
                    Template tour
                  </Button>
                  <Button
                    ref={templateSaveButtonRef}
                    className="profile-heading-save-button"
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving || uploading}
                  >
                    Save
                  </Button>
                </div>
              </div>
              <div className="profile-template-grid">
                <div className="profile-template-item" ref={resumeTemplateRef}>
                  <div className="profile-template-label">
                    <label>Resume template</label>
                    {templateUploadErrors.resume.length > 0 && (
                      <Tooltip
                        open={visibleErrorTooltip === "resume"}
                        onOpenChange={(open) =>
                          setVisibleErrorTooltip(open ? "resume" : null)
                        }
                        placement="topRight"
                        title={
                          <div className="template-error-tooltip">
                            <strong>Template validation errors</strong>
                            <ul>
                              {templateUploadErrors.resume.map(
                                (error, index) => (
                                  <li key={`${error}-${index}`}>{error}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        }
                      >
                        <button
                          className="template-error-icon"
                          type="button"
                          aria-label="Show resume template validation errors"
                          onClick={() => setVisibleErrorTooltip(null)}
                        >
                          <WarningFilled />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="profile-template-content">
                    <Dragger {...uploadProps("resume")}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag a DOCX file here
                      </p>
                      <p className="ant-upload-hint">
                        Replace the template used for resumes.
                      </p>
                    </Dragger>
                    <aside className="template-preview-aside template-preview-tip-compact">
                      <div className="template-preview-tip">
                        <InfoCircleOutlined />
                        <div>
                          <strong>Resume template preview</strong>
                          <span>
                            Open the uploaded DOCX with its original formatting.
                          </span>
                        </div>
                      </div>
                      <Button
                        size="small"
                        className="template-preview-button"
                        icon={<EyeOutlined />}
                        onClick={() => setTemplatePreview("resume")}
                      >
                        Compare resume
                      </Button>
                    </aside>
                  </div>
                </div>
                <div
                  className="profile-template-item"
                  ref={coverLetterTemplateRef}
                >
                  <div className="profile-template-label">
                    <label>Cover letter template</label>
                    {templateUploadErrors.coverLetter.length > 0 && (
                      <Tooltip
                        open={visibleErrorTooltip === "coverLetter"}
                        onOpenChange={(open) =>
                          setVisibleErrorTooltip(open ? "coverLetter" : null)
                        }
                        placement="topRight"
                        title={
                          <div className="template-error-tooltip">
                            <strong>Template validation errors</strong>
                            <ul>
                              {templateUploadErrors.coverLetter.map(
                                (error, index) => (
                                  <li key={`${error}-${index}`}>{error}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        }
                      >
                        <button
                          className="template-error-icon"
                          type="button"
                          aria-label="Show cover letter template validation errors"
                          onClick={() => setVisibleErrorTooltip(null)}
                        >
                          <WarningFilled />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="profile-template-content">
                    <Dragger {...uploadProps("coverLetter")}>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag a DOCX file here
                      </p>
                      <p className="ant-upload-hint">
                        Replace the template used for cover letters.
                      </p>
                    </Dragger>
                    <aside className="template-preview-aside template-preview-tip-compact">
                      <div className="template-preview-tip">
                        <InfoCircleOutlined />
                        <div>
                          <strong>Cover letter preview</strong>
                          <span>
                            Open the uploaded DOCX with its original formatting.
                          </span>
                        </div>
                      </div>
                      <Button
                        size="small"
                        className="template-preview-button"
                        icon={<EyeOutlined />}
                        onClick={() => setTemplatePreview("coverLetter")}
                      >
                        Compare cover letter
                      </Button>
                    </aside>
                  </div>
                </div>
              </div>
              <div
                className="profile-employment-section"
                ref={employmentSettingsRef}
              >
                <div className="profile-employment-section-title">
                  <strong>
                    <ExperimentOutlined /> Employment settings
                  </strong>
                  <span>
                    Configure experience level, employment order, and resume
                    bullet counts.
                  </span>
                </div>
                <div className="profile-employment-heading">
                  <div>
                    <strong>
                      Employment count: {employmentPreferences.length}
                    </strong>
                    <span>
                      Choose a title prefix and the number of bullets for each
                      employment entry.
                    </span>
                  </div>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setEmploymentPreferences((entries) => [
                        ...entries,
                        DEFAULT_EMPLOYMENT(),
                      ])
                    }
                  >
                    Add employment
                  </Button>
                </div>
                <label className="profile-years-experience">
                  <span>Years of experience</span>
                  <InputNumber
                    min={0}
                    max={100}
                    precision={0}
                    value={yearsOfExperience}
                    onChange={(value) => setYearsOfExperience(value)}
                  />
                </label>
                <div className="profile-employment-list">
                  {employmentPreferences.map((employment, index) => (
                    <div className="profile-employment-row" key={index}>
                      <strong>Employment {index + 1}</strong>
                      <label>
                        <span>Title prefix</span>
                        <Select
                          value={employment.title_prefix || undefined}
                          placeholder="Select title prefix"
                          options={TITLE_PREFIXES.map((prefix) => ({
                            label: prefix,
                            value: prefix,
                          }))}
                          onChange={(title_prefix) =>
                            setEmploymentPreferences((entries) =>
                              entries.map((entry, entryIndex) =>
                                entryIndex === index
                                  ? { ...entry, title_prefix }
                                  : entry,
                              ),
                            )
                          }
                        />
                      </label>
                      <label>
                        <span>Bullet count</span>
                        <InputNumber
                          min={1}
                          max={50}
                          precision={0}
                          value={employment.bullet_count}
                          onChange={(bullet_count) =>
                            setEmploymentPreferences((entries) =>
                              entries.map((entry, entryIndex) =>
                                entryIndex === index
                                  ? { ...entry, bullet_count }
                                  : entry,
                              ),
                            )
                          }
                        />
                      </label>
                      <Button
                        type="text"
                        danger
                        aria-label={`Remove employment ${index + 1}`}
                        icon={<MinusCircleOutlined />}
                        disabled={employmentPreferences.length <= 2}
                        onClick={() =>
                          setEmploymentPreferences((entries) =>
                            entries.filter(
                              (_, entryIndex) => entryIndex !== index,
                            ),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {canUseSpecialActions && (
              <section className="profile-editor-card">
                <div className="profile-card-heading">
                  <div>
                    <strong>
                      <RobotOutlined /> AI prompts
                    </strong>
                    <span>
                      Customize the instructions used when generating bids and
                      call responses.
                    </span>
                  </div>
                </div>
                <div className="profile-prompt-grid">
                  <label>
                    <span>Bid prompt</span>
                    <small>Instructions for bid generation.</small>
                    <TextArea
                      value={personalPrompt}
                      onChange={(event) =>
                        setPersonalPrompt(event.target.value)
                      }
                      autoSize={{ minRows: 12, maxRows: 24 }}
                    />
                  </label>
                  <label>
                    <span>Call prompt</span>
                    <small>
                      Instructions for interview and call responses.
                    </small>
                    <TextArea
                      value={callPrompt}
                      onChange={(event) => setCallPrompt(event.target.value)}
                      autoSize={{ minRows: 12, maxRows: 24 }}
                    />
                  </label>
                </div>
              </section>
            )}

            <div className="profile-actions">
              <span>
                Templates, employment settings, and prompts will be updated.
              </span>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving || uploading}
              >
                Save Profile Content
              </Button>
            </div>
          </Form>
        )}
      </div>
      <Tour
        rootClassName="profile-template-tour"
        open={templateTourOpen}
        onClose={closeTemplateTour}
        onFinish={closeTemplateTour}
        steps={[
          {
            title: "Set up your document templates",
            description: (
              <div className="template-tour-intro">
                <span className="template-tour-intro-icon">
                  <FileTextOutlined />
                </span>
                <p>
                  Resume Builder inserts tailored AI content into your own DOCX
                  design, preserving its formatting.
                </p>
                <strong>
                  Upload and save both templates before building your first
                  resume.
                </strong>
              </div>
            ),
          },
          {
            title: "Upload your resume DOCX template",
            description: (
              <div className="template-tour-copy">
                <p>Use supported replaceable variables in your DOCX:</p>
                <div className="template-tour-variables">
                  <code>{"{role_title}"}</code>
                  <code>{"{role1}"}</code>
                  <code>experience loops</code>
                  <code>skills list</code>
                </div>
                <p>
                  Generated resume data replaces these values while your
                  document layout stays intact.
                </p>
              </div>
            ),
            target: () => resumeTemplateRef.current,
          },
          {
            title: "Upload your cover-letter DOCX template",
            description: (
              <div className="template-tour-copy">
                <p>
                  The cover-letter template uses its content variable for the
                  generated tailored letter.
                </p>
                <p>
                  Upload a DOCX, then select{" "}
                  <strong>Compare cover letter</strong> to review it before
                  saving.
                </p>
              </div>
            ),
            target: () => coverLetterTemplateRef.current,
          },
          {
            title: "Configure employment settings",
            description: (
              <div className="template-tour-copy">
                <p>
                  Add one entry for each employment section represented in your
                  resume template.
                </p>
                <ul className="template-tour-list">
                  <li>
                    <strong>Title prefix</strong> guides the seniority used for
                    each generated role.
                  </li>
                  <li>
                    <strong>Bullet count</strong> controls how many tailored
                    experience bullets AI creates for that employment.
                  </li>
                  <li>
                    Employment 1 maps to <code>{"{role1}"}</code> and its
                    experience loop; Employment 2 maps to the role 2 fields, and
                    so on.
                  </li>
                </ul>
              </div>
            ),
            target: () => employmentSettingsRef.current,
          },
          {
            title: "Save and start building",
            description: (
              <div className="template-tour-finish">
                <strong>Your setup checklist</strong>
                <span>
                  <CheckCircleFilled /> Resume DOCX uploaded
                </span>
                <span>
                  <CheckCircleFilled /> Cover-letter DOCX uploaded
                </span>
                <span>
                  <CheckCircleFilled /> Employment settings configured
                </span>
                <p>
                  Select <b>Save</b> to store this setup. You can then build,
                  review, and finalize tailored resumes from Build Resume or the
                  Job Board.
                </p>
              </div>
            ),
            target: () => templateSaveButtonRef.current,
          },
        ]}
      />
      <Modal
        className="template-preview-modal"
        title={
          templatePreview === "resume"
            ? "Resume template comparison"
            : "Cover letter template comparison"
        }
        open={Boolean(templatePreview)}
        onCancel={() => setTemplatePreview(null)}
        footer={null}
        width={1500}
        centered
        destroyOnHidden
      >
        <div className="template-preview-split">
          <div
            className={`docx-preview-viewer ${templatePreview !== "resume" ? "comparison-hidden" : ""}`}
          >
            <div className="docx-preview-toolbar">
              <span className="docx-file-icon">W</span>
              <div>
                <strong>{resumeFile || "Example resume template"}</strong>
                <span>
                  {resumeFile ? "Uploaded resume" : "Example preview"}
                </span>
              </div>
              <Tag>UPLOADED</Tag>
            </div>
            <div className="docx-preview-canvas">
              {!isEmpty(resumeFile) ? (
                <iframe
                  title="Uploaded resume template preview"
                  src={`${API_BASE_URL}/api/bids/template-preview/${encodeURIComponent(resumeFile)}#navpanes=0&view=FitH`}
                />
              ) : (
                <div className="template-preview-empty">
                  <FileTextOutlined />
                  <strong>No resume uploaded yet</strong>
                  <span>
                    Upload a DOCX file to compare it with this example.
                  </span>
                </div>
                /*
                <div className="document-preview resume-document-preview">
                  <strong className="document-preview-name">
                    {user?.name || "James Roberts"}
                  </strong>
                  <b>{"{role_title}"}</b>
                  <span>
                    Location | {user?.email || "email@example.com"} | Phone
                    number
                  </span>
                  <h4>Professional Summary</h4>
                  <p>{"{summary}"}</p>
                  <h4>Professional Experience</h4>
                  {employmentPreferences.map((_employment, index) => (
                    <div className="document-preview-employment" key={index}>
                      <b>{`{role${index + 1}}`}</b>
                      <span>Company | Location | Employment dates</span>
                      <small>{`{#experienceList${index + 1}}`}</small>
                      <p>&bull; {`{experience${index + 1}}`}</p>
                      <small>{`{/experienceList${index + 1}}`}</small>
                    </div>
                  ))}
                  <h4>Technical Skills</h4>
                  <p>
                    {"{#skillsList}"} &nbsp; &bull; &nbsp;{" "}
                    {"{category}: {items}"} &nbsp; {"{/skillsList}"}
                  </p>
                  <h4>Education</h4>
                  <b>Degree name</b>
                  <p>University name, location | Start date - End date</p>
                </div> */
              )}
            </div>
            <div className="docx-preview-status">
              Resume &nbsp; &bull; &nbsp; Preview only
            </div>
          </div>
          <div
            className={`docx-preview-viewer ${templatePreview !== "coverLetter" ? "comparison-hidden" : ""}`}
          >
            <div className="docx-preview-toolbar">
              <span className="docx-file-icon">W</span>
              <div>
                <strong>
                  {coverLetterFile || "Example cover letter template"}
                </strong>
                <span>
                  {coverLetterFile
                    ? "Uploaded cover letter"
                    : "Example preview"}
                </span>
              </div>
              <Tag>UPLOADED</Tag>
            </div>
            <div className="docx-preview-canvas">
              {!isEmpty(coverLetterFile) ? (
                <iframe
                  title="Uploaded cover letter template preview"
                  src={`${API_BASE_URL}/api/bids/template-preview/${encodeURIComponent(coverLetterFile)}#navpanes=0&view=FitH`}
                />
              ) : (
                <div className="template-preview-empty">
                  <FileTextOutlined />
                  <strong>No cover letter uploaded yet</strong>
                  <span>
                    Upload a DOCX file to compare it with this example.
                  </span>
                </div>
                /* <div className="document-preview cover-document-preview">
                  <p>Dear Hiring Manager,</p>
                  <p>{"{content}"}</p>
                  <p>
                    Sincerely,
                    <br />
                    {user?.name || "Your name"}
                  </p>
                </div> */
              )}
            </div>
            <div className="docx-preview-status">
              Cover letter &nbsp; &bull; &nbsp; Preview only
            </div>
          </div>
          <div className="docx-preview-viewer">
            <div className="docx-preview-toolbar">
              <span className="docx-file-icon">W</span>
              <div>
                <strong>
                  {templatePreview === "resume"
                    ? "Example resume template"
                    : "Example cover letter template"}
                </strong>
                <span>Recommended HTML example</span>
              </div>
              <Tag>EXAMPLE</Tag>
            </div>
            <div className="docx-preview-canvas example-preview-canvas">
              {templatePreview === "resume" ? (
                <div className="document-preview resume-document-preview">
                  <strong className="document-preview-name">
                    {user?.name || "Your Name"}
                  </strong>
                  <b>{"{role_title}"}</b>
                  <span>
                    Location | {user?.email || "email@example.com"} | Phone
                    number
                  </span>
                  <h4>Professional Summary</h4>
                  <p>{"{summary}"}</p>
                  <h4>Professional Experience</h4>
                  {employmentPreferences.map((_employment, index) => (
                    <div className="document-preview-employment" key={index}>
                      <b>{`{role${index + 1}}`}</b>
                      <span>Company | Location | Employment dates</span>
                      <small>{`{#experienceList${index + 1}}`}</small>
                      <p>&bull; {`{experience${index + 1}}`}</p>
                      <small>{`{/experienceList${index + 1}}`}</small>
                    </div>
                  ))}
                  <h4>Technical Skills</h4>
                  <p>
                    {"{#skillsList}"} &nbsp; &bull; &nbsp;{" "}
                    {"{category}: {items}"} &nbsp; {"{/skillsList}"}
                  </p>
                  <h4>Education</h4>
                  <b>Degree name</b>
                  <p>University name, location | Start date - End date</p>
                </div>
              ) : (
                <div className="document-preview cover-document-preview">
                  <p>Dear Hiring Manager,</p>
                  <p>{"{content}"}</p>
                  <p>
                    Sincerely,
                    <br />
                    {user?.name || "Your name"}
                  </p>
                </div>
              )}
            </div>
            <div className="docx-preview-status">
              Example template &nbsp; &bull; &nbsp; Compare with your upload
            </div>
          </div>
        </div>
        <div className="docx-preview-viewer legacy-template-preview">
          <div className="docx-preview-toolbar">
            <span className="docx-file-icon">W</span>
            <div>
              <strong>
                {templatePreview === "resume" ? resumeFile : coverLetterFile}
              </strong>
              <span>Microsoft Word document preview</span>
            </div>
            <Tag>DOCX</Tag>
          </div>
          <div className="docx-preview-canvas">
            <iframe
              title="Uploaded DOCX template preview"
              src={`${API_BASE_URL}/api/bids/template-preview/${encodeURIComponent(templatePreview === "resume" ? resumeFile : coverLetterFile)}`}
            />
            {templatePreview === "resume" ? (
              <div className="document-preview resume-document-preview">
                <strong className="document-preview-name">
                  {user?.name || "Your Name"}
                </strong>
                <b>{"{role_title}"}</b>
                <span>
                  New York, New York, US | {user?.email || "email@example.com"}{" "}
                  | Phone number
                </span>
                <h4>Professional Summary</h4>
                <p>{"{summary}"}</p>
                <h4>Professional Experience</h4>
                {employmentPreferences.map((_employment, index) => (
                  <div className="document-preview-employment" key={index}>
                    <b>{`{role${index + 1}}`}</b>
                    <span>Company | Location | Employment dates</span>
                    <small>{`{#experienceList${index + 1}}`}</small>
                    <p>&bull; {`{experience${index + 1}}`}</p>
                    <small>{`{/experienceList${index + 1}}`}</small>
                  </div>
                ))}
                <h4>Technical Skills</h4>
                <p>
                  {"{#skillsList}"} &nbsp; &bull; &nbsp; {"{category}: {items}"}{" "}
                  &nbsp; {"{/skillsList}"}
                </p>
                <h4>Education</h4>
                <b>Bachelor&rsquo;s Degree in Computer Science</b>
                <p>
                  University of California | California, US | Apr 2013 - Sep
                  2017
                </p>
              </div>
            ) : (
              <div className="document-preview cover-document-preview">
                <p>Dear Hiring Manager,</p>
                <p>{"{content}"}</p>
                <p>
                  Sincerely,
                  <br />
                  {user?.name || "Your Name"}
                </p>
              </div>
            )}
          </div>
          <div className="docx-preview-status">
            Page 1 of 1 &nbsp; • &nbsp; Preview only
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
